import React, { useContext } from "react"

import { useCommonProperty } from "shared/lib/useCommonProperties"
import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"
import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  FloorTypename,
  ProductPositionTypename,
  ProjectChild,
  RoomTypename
} from "shared/types/Project"
import { Room } from "shared/types/Room"
import { TableAPIContext } from "shared/ui/table/TableAPIContext"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { Modal } from "widgets/modal/Modal"

import { ProjectPageFloorSidebarConnector } from "./floor/ProjectPageFloorSidebarConnector"
import { UseProjectPageFloorSidebarModelArgs } from "./floor/hooks/useProjectPageFloorSidebarModel"
import { ProductPositionMultiSelectSidebarConnector } from "./multi/product-position/ProductPositionMultiSelectSidebarConnector"
import { UseProductPositionMultiSelectSidebarModelArgs } from "./multi/product-position/hooks/useProductPositionMultiSelectSidebarModel"
import { ProjectChildMultiSelectSidebarConnector } from "./multi/project-child/ProjectChildMultiSelectSidebarConnector"
import { UseProjectChildMultiSelectSidebarModelArgs } from "./multi/project-child/hooks/useProjectChildMultiSelectSidebarModel"
import { RoomMultiSelectSidebarConnector } from "./multi/room/RoomMultiSelectSidebarConnector"
import { UseRoomMultiSelectSidebarModelArgs } from "./multi/room/hooks/useRoomMultiSelectSidebarModel"
import { ProjectPageProductPositionSidebarConnector } from "./product-position/ProjectPageProductPositionSidebarConnector"
import { UseProjectPageProductPositionSidebarModelArgs } from "./product-position/hooks/useProjectPageProductPositionSidebarModel"
import { ProjectPageProjectSidebarConnector } from "./project/ProjectPageProjectSidebarConnector"
import { ProjectPageRoomSidebarConnector } from "./room/ProjectPageRoomSidebarConnector"
import { UseProjectPageRoomSidebarModelArgs } from "./room/hooks/useProjectPageRoomSidebarModel"

export interface ProjectPageSidebarFactoryComponents {
  ProjectSidebar?: React.ComponentType<{ projectID: string }>
  FloorSidebar?: React.ComponentType<UseProjectPageFloorSidebarModelArgs>
  ProductPositionSidebar?: React.ComponentType<UseProjectPageProductPositionSidebarModelArgs>
  ProductPositionMultiSelectSidebar?: React.ComponentType<UseProductPositionMultiSelectSidebarModelArgs>
  RoomSidebar?: React.ComponentType<UseProjectPageRoomSidebarModelArgs>
  RoomMultiSelectSidebar?: React.ComponentType<UseRoomMultiSelectSidebarModelArgs>
  ProjectChildMultiSelect?: React.ComponentType<UseProjectChildMultiSelectSidebarModelArgs>
}

export interface ProjectPageSidebarFactoryProps {
  projectID: string
  selectedChildren: ProjectChild[]
  components?: ProjectPageSidebarFactoryComponents
}

interface ProjectSidebarInnerProps {
  projectID: string
  selectedChildren: ProjectChild[]
  selectedElementIDs: string[]
  ProjectSidebar?: React.ComponentType<{ projectID: string }>
  FloorSidebarComponent: React.ComponentType<UseProjectPageFloorSidebarModelArgs>
  ProductPositionSidebarComponent: React.ComponentType<UseProjectPageProductPositionSidebarModelArgs>
  ProductPositionMultiSelectSidebarComponent: React.ComponentType<UseProductPositionMultiSelectSidebarModelArgs>
  RoomSidebarComponent: React.ComponentType<UseProjectPageRoomSidebarModelArgs>
  RoomMultiSelectSidebarComponent: React.ComponentType<UseRoomMultiSelectSidebarModelArgs>
  ProjectChildMultiSelectComponent: React.ComponentType<UseProjectChildMultiSelectSidebarModelArgs>
}

export const ProjectSidebarInner = React.memo(
  ({
    selectedChildren,
    projectID,
    selectedElementIDs,
    ProjectSidebar,
    FloorSidebarComponent,
    ProductPositionSidebarComponent,
    ProductPositionMultiSelectSidebarComponent,
    RoomSidebarComponent,
    RoomMultiSelectSidebarComponent,
    ProjectChildMultiSelectComponent
  }: ProjectSidebarInnerProps) => {
    const { __typename: commonTypename } = useCommonProperty({
      objects: selectedChildren,
      key: "__typename"
    })

    if (selectedChildren.length === 1) {
      const selectedElement = selectedChildren[0]

      if (selectedElement?.__typename === FloorTypename) {
        return (
          <FloorSidebarComponent
            key={selectedElement.id}
            floor={selectedElement as Floor}
            projectID={projectID}
          />
        )
      }

      if (selectedElement?.__typename === RoomTypename) {
        return (
          <RoomSidebarComponent
            key={selectedElement.id}
            room={selectedElement}
            projectID={projectID}
          />
        )
      }
      if (selectedElement?.__typename === ProductPositionTypename) {
        return (
          <ProductPositionSidebarComponent
            key={selectedElement.id}
            productPosition={selectedElement}
            projectID={projectID}
          />
        )
      }

      return null
    }
    if (selectedChildren.length > 1) {
      if (commonTypename === ProductPositionTypename) {
        return (
          <ProductPositionMultiSelectSidebarComponent
            productPositions={selectedChildren as ProductPosition[]}
            projectID={projectID}
          />
        )
      }
      if (commonTypename === RoomTypename) {
        return (
          <RoomMultiSelectSidebarComponent
            rooms={selectedChildren as Room[]}
            projectID={projectID}
          />
        )
      }

      return (
        <ProjectChildMultiSelectComponent
          projectID={projectID}
          selectedChildren={selectedChildren}
        />
      )
    }

    if (
      ProjectSidebar &&
      projectID !== FAVORITES_PROJECT_ID &&
      selectedElementIDs.length === 0
    ) {
      return <ProjectSidebar projectID={projectID} />
    }

    return null
  }
)

export const ProjectPageSidebarFactory = React.memo(
  ({
    projectID,
    selectedChildren,
    components
  }: ProjectPageSidebarFactoryProps) => {
    const {
      ProjectSidebar = ProjectPageProjectSidebarConnector,
      FloorSidebar = ProjectPageFloorSidebarConnector,
      ProductPositionSidebar = ProjectPageProductPositionSidebarConnector,
      ProductPositionMultiSelectSidebar = ProductPositionMultiSelectSidebarConnector,
      RoomSidebar = ProjectPageRoomSidebarConnector,
      RoomMultiSelectSidebar = RoomMultiSelectSidebarConnector,
      ProjectChildMultiSelect = ProjectChildMultiSelectSidebarConnector
    } = components ?? {}

    const { isTablet } = useScreenSize()
    const { tableAPIRef } = useContext(TableAPIContext)
    const { selectedElementIDs, isDetailsViewOpen, closeElementDetails } =
      useProjectStore(projectID)

    const handleAfterClose = useEvent(() => {
      if (selectedElementIDs[0]) {
        tableAPIRef?.current?.scrollToElement(selectedElementIDs[0])
      }
    })

    let isHidden = false

    if (selectedChildren.length === 0 && selectedElementIDs.length > 0) {
      isHidden = true
    }

    if (selectedElementIDs.length === 0 && isTablet && !isDetailsViewOpen) {
      isHidden = true
    }

    return (
      <Modal
        preventPortal={!isTablet}
        onClose={closeElementDetails}
        onAfterClose={handleAfterClose}
        isHidden={isHidden}
      >
        <ProjectSidebarInner
          projectID={projectID}
          selectedChildren={selectedChildren}
          selectedElementIDs={selectedElementIDs}
          ProjectSidebar={ProjectSidebar}
          FloorSidebarComponent={FloorSidebar}
          ProductPositionSidebarComponent={ProductPositionSidebar}
          ProductPositionMultiSelectSidebarComponent={
            ProductPositionMultiSelectSidebar
          }
          RoomSidebarComponent={RoomSidebar}
          RoomMultiSelectSidebarComponent={RoomMultiSelectSidebar}
          ProjectChildMultiSelectComponent={ProjectChildMultiSelect}
        />
      </Modal>
    )
  }
)
